import {
    AddOnType,
    CreatePointRequest,
    CreatePointsResponse,
    HOTRow,
    HandsontableCore,
    HandsontableNamespace,
    Location,
    TopicCategory,
    UserTag,
    uploadImageUrlRequest
} from '../types';
import { HandsontableElement, getCannotBeEmptyValidator, getCustomFieldValidator, getDateValidator, getMaxLengthValidator, getPointNumberValidator, getTagValidator } from './wrappers/handsontable-element';
import { LitElement, css, html } from 'lit';
import { addPoints, deletePoint, invalidatePointImagesCache, updatePoints } from '../helpers/api/points-api';
import { createDummyPoint, createErrorStringForBulk, createHOTRowsFromPoints, pointRequestFromHOTRow } from '../helpers/components/hot-points';
import { customElement, query, state } from 'lit/decorators';
import { delay, isDefined, isEmpty, isValidString, undefinedIfEmpty } from '../helpers/utils';
import { isLocationInputLatLong, timezoneAsync } from '../helpers/api/google-api';

import { ColumnSettings } from 'handsontable/settings';
import { StateController } from '../helpers/state/state-management/state-controller';
import { chunk } from '../helpers/math';
import featuresInfo from '../helpers/state/features-info';
import { getCategoryIcon } from '../helpers/markers';
import { getMainPage } from '../helpers/page-name';
import { getVisibleTextColor } from '../helpers/colors';
import pointCache from '../helpers/state/point-cache';
import { removeInertFromLocationElement } from '../helpers/inert';
import topicData from '../helpers/state/topic-data';

// Allows us to use `Handsontable` as a global.
declare const Handsontable: HandsontableNamespace;

// Representes change information from the HOT `afterChange` event.
interface HotChange {
    row: number;
    prop: string;
    oldValue: any;
    newValue: any;
}

// Represents the type of change being tracked.
enum ChangeType {
    Create,
    Update,
    Delete,
}

// Represents a change that is tracked from the spreadsheet.
interface Change {
    id: string;
    point: HOTRow | undefined;
    changeType: ChangeType;
}

@customElement('hot-points')
export class HotPointsElement extends LitElement {
    static amplitude_callsite: string = 'Handsontable Points';



    @query('#handsontable')
    handsontableElement!: HandsontableElement;

    @state()
    private uploadInProgress: boolean = false;

    @state()
    findingDuplicates: boolean = false;

    @state()
    showDuplicateButton: boolean = true;

    @state()
    wordWrap: boolean = false;

    /**
     * Per-point IANA timezone, keyed by `lat,lng` (rounded for cache
     * stability). Populated once at table load via
     * `resolveTimezonesForPoints`; consulted synchronously by the cell
     * renderer and the cell-edit handlers so every row's typed value is
     * interpreted in the right zone. Rows whose location hasn't been
     * resolved yet (newly added or freshly geocoded) fall back to the
     * browser zone with a visible "(your browser)" badge so admins know
     * the row's tz hasn't been confirmed.
     */
    private readonly pointTimezones: Map<string, string> = new Map();

    /**
     * Browser's IANA zone — used as a transparent fallback for rows
     * whose location-tz hasn't been resolved yet (or where lookup
     * failed). Frozen at construction so it can't drift mid-edit.
     */
    private readonly browserTimezone: string = HotPointsElement.detectBrowserTimezone();

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    hotPoints: HOTRow[] = [];

    private static detectBrowserTimezone(): string {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        } catch {
            return 'UTC';
        }
    }

    /**
     * Cache key for `pointTimezones`. Round to 4 decimals (~11 m) so
     * tiny lat/lng nudges from re-geocodes don't produce duplicate
     * entries; tz boundaries are far coarser than 11 m so this is safe.
     */
    private static tzKey(lat: number, lng: number): string {
        return `${lat.toFixed(4)},${lng.toFixed(4)}`;
    }

    /**
     * Best-effort sync lookup: returns the resolved IANA zone for a
     * point's location, or the browser fallback when none is cached.
     * Returns the IANA name plus a flag so the renderer can label
     * fallback rows differently.
     */
    private timezoneForLocation(location: Location | undefined | null): { tz: string; resolved: boolean } {
        const latLng = location?.latLng;
        if (!latLng || typeof latLng.lat !== 'number' || typeof latLng.lng !== 'number') {
            return { tz: this.browserTimezone, resolved: false };
        }
        const key = HotPointsElement.tzKey(latLng.lat, latLng.lng);
        const cached = this.pointTimezones.get(key);
        if (cached !== undefined) return { tz: cached, resolved: true };
        return { tz: this.browserTimezone, resolved: false };
    }

    /**
     * Lat/lng keys whose tz lookup is currently in flight — prevents
     * the renderer from kicking off duplicate requests when the same
     * row gets redrawn before the first network call returns.
     */
    private readonly pendingTzLookups: Set<string> = new Set();

    /** Whether we've queued a re-render for the next frame. */
    private tzRenderScheduled: boolean = false;

    /**
     * Fire a background tz lookup for a lat/lng if we don't already
     * have one cached or in flight. When it resolves, store it and
     * schedule a single re-render so every visible cell that depends
     * on it picks up the new value at once. Failure is silent — the
     * renderer falls back to browser zone with a "(your browser)"
     * badge so users know it's not authoritative.
     */
    private kickOffTimezoneLookup(latLng: { lat: number; lng: number }) {
        const key = HotPointsElement.tzKey(latLng.lat, latLng.lng);
        if (this.pointTimezones.has(key) || this.pendingTzLookups.has(key)) return;
        this.pendingTzLookups.add(key);
        void timezoneAsync(latLng.lat, latLng.lng)
            .then((infos) => {
                const id = infos[0]?.identifier;
                if (id !== undefined && id !== '') {
                    this.pointTimezones.set(key, id);
                }
            })
            .catch(() => {
                // Lookup failure is non-fatal.
            })
            .finally(() => {
                this.pendingTzLookups.delete(key);
                this.scheduleTimezoneRender();
            });
    }

    /**
     * Coalesce many tz lookups completing in the same frame into a
     * single Handsontable re-render. Without this, a 200-point map
     * would trigger 200 cascading renders as each lookup resolves.
     */
    private scheduleTimezoneRender() {
        if (this.tzRenderScheduled) return;
        this.tzRenderScheduled = true;
        requestAnimationFrame(() => {
            this.tzRenderScheduled = false;
            try {
                this.handsontableElement?.hot?.render();
            } catch {
                // table may have torn down between schedule and frame.
            }
        });
    }

    /**
     * Format a stored UTC ISO string in the supplied timezone for the
     * cell's local-time subtitle. Picks date-only, time-only, or full
     * datetime formatting based on the topic's settings, and appends
     * the short tz label ("PDT", "EDT", etc.) so the user can see at a
     * glance what the value reads as on the ground. Returns an empty
     * string when the input can't be parsed.
     */
    private formatLocalSubtitle(
        iso: string | undefined | null,
        tz: string,
        dateOnlyDisplay: boolean = false,
    ): string {
        if (iso === undefined || iso === null || iso === '') return '';
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) return '';
        const dateOnlyTopic = topicData.topic?.has_date_only === true;
        const timeOnlyTopic = topicData.topic?.has_time_only === true;
        try {
            const opts: Intl.DateTimeFormatOptions = { timeZone: tz };
            if (!timeOnlyTopic) {
                opts.year = 'numeric';
                opts.month = 'short';
                opts.day = 'numeric';
            }
            // Time + tz label come along unless the topic is date-only
            // or the renderer asked for a date-only display (e.g. an
            // all-day row, where "May 31, 2026 PDT" reads weirdly).
            if (!dateOnlyTopic && !dateOnlyDisplay) {
                opts.hour = 'numeric';
                opts.minute = '2-digit';
                opts.timeZoneName = 'short';
            }
            return new Intl.DateTimeFormat(undefined, opts).format(date);
        } catch {
            return '';
        }
    }

    /**
     * Hour + minute of an ISO string interpreted in the supplied IANA
     * tz, or undefined if the string can't be parsed. Used by the
     * renderer to decide whether a row is genuinely all-day (start at
     * 00:00 AND end at 23:59 in the row's tz) — labelling on a single
     * boundary alone is misleading, since a real event happening to
     * start at midnight isn't all-day on its own.
     */
    private localHourMinute(
        iso: unknown,
        tz: string,
    ): { hour: number; minute: number } | undefined {
        if (iso === undefined || iso === null) return undefined;
        const trimmed = String(iso).trim();
        if (trimmed === '') return undefined;
        const date = new Date(trimmed);
        if (Number.isNaN(date.getTime())) return undefined;
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: tz,
                hour: 'numeric',
                minute: 'numeric',
                hour12: false,
            }).formatToParts(date);
            const hourStr = parts.find((p) => p.type === 'hour')?.value;
            const minuteStr = parts.find((p) => p.type === 'minute')?.value;
            if (hourStr === undefined || minuteStr === undefined) return undefined;
            return { hour: parseInt(hourStr, 10), minute: parseInt(minuteStr, 10) };
        } catch {
            return undefined;
        }
    }

    /**
     * Cell renderer for the start / end date columns. The cell's
     * editable value stays exactly what's stored on the server (UTC
     * ISO, ending in 'Z') — pasting and round-tripping CSV exports
     * keeps working unchanged. Below the raw value we render the same
     * instant in the point's location timezone, so admins can verify
     * at a glance that "Z" in fact means "their local 7 PM" without
     * having to do the math themselves.
     */
    private readonly renderDateCell = (
        instance: { getDataAtRowProp: (row: number, prop: string) => unknown },
        td: HTMLElement,
        row: number,
        _col: number,
        prop: unknown,
        value: unknown,
        _cellProperties: unknown,
    ) => {
        // Reset the cell — Handsontable reuses TDs across renders.
        td.innerHTML = '';
        if (value === undefined || value === null) return;
        const trimmed = String(value).trim();
        if (trimmed === '') return;

        // Use textContent (not innerHTML) so any user-supplied content is
        // safe from injection.
        const main = document.createElement('div');
        main.textContent = trimmed;
        td.appendChild(main);

        const location = instance.getDataAtRowProp(row, 'location') as Location | undefined;
        const { tz, resolved } = this.timezoneForLocation(location);
        // Lazy-load the row's location tz the first time its cell
        // renders, rather than fetching every point's tz upfront. The
        // initial render uses the browser fallback; once the lookup
        // resolves, we re-render and the cell upgrades to the actual
        // location tz. Handsontable only renders visible rows, so
        // off-screen points don't burn requests until the user scrolls.
        const latLng = location?.latLng;
        if (!resolved && latLng && typeof latLng.lat === 'number' && typeof latLng.lng === 'number') {
            this.kickOffTimezoneLookup({ lat: latLng.lat, lng: latLng.lng });
        }

        // Decide whether the row is genuinely all-day by checking BOTH
        // start and end against the boundaries in the row's tz. A start
        // happening to land on midnight without a matching 23:59 end
        // isn't all-day (it's a real midnight event); collapsing it
        // would lose info. So we only suppress the time + tag "all day"
        // when both halves line up.
        const propName = typeof prop === 'string' ? prop : '';
        const isStartCell = propName === 'startString';
        const startValue = isStartCell ? trimmed : instance.getDataAtRowProp(row, 'startString');
        const endValue = isStartCell ? instance.getDataAtRowProp(row, 'endString') : trimmed;
        const startLocal = this.localHourMinute(startValue, tz);
        const endLocal = this.localHourMinute(endValue, tz);
        const isAllDayRow =
            startLocal !== undefined
            && endLocal !== undefined
            && startLocal.hour === 0 && startLocal.minute === 0
            && endLocal.hour === 23 && endLocal.minute === 59;

        const localText = this.formatLocalSubtitle(trimmed, tz, isAllDayRow);
        if (localText === '') return;
        const taggedText = isAllDayRow ? `${localText} · all day` : localText;

        const sub = document.createElement('div');
        // Inline styles because Handsontable's TDs render outside Lit's
        // shadow DOM, so class selectors in `static styles` don't reach
        // them. Whitespace nowrap keeps the local-time line from
        // wrapping into the next row when the column is narrow.
        sub.style.fontSize = '0.72em';
        sub.style.color = '#888';
        sub.style.marginTop = '2px';
        sub.style.whiteSpace = 'nowrap';
        // When the row's tz couldn't be resolved (no location, lookup
        // failure), tag the subtitle so users know the local time
        // shown is computed against their browser zone, not the
        // point's actual location.
        sub.textContent = resolved ? taggedText : `${taggedText} (your browser)`;
        td.appendChild(sub);
    };

    get categoryIdMap() {
        return topicData.categories.reduce((acc, category) => {
            if (category._id?.$oid !== undefined) {
                acc.set(category._id.$oid, category);
                return acc;
            } else {
                return acc;
            }
        }
        , new Map<string, TopicCategory>()) ?? new Map<string, TopicCategory>();
    }

    get categoryNameMap() {
        return topicData.categories.reduce((acc, category) => {
            if (category.name !== undefined) {
                acc.set(category.name.trim().toLowerCase(), category);
                return acc;
            } else {
                return acc;
            }
        }
        , new Map<string, TopicCategory>()) ?? new Map<string, TopicCategory>();
    }

    getCategoryFromMap(name: string): TopicCategory | undefined {
        if (name) {
            const normalizedKey = name.trim().toLowerCase();
            return this.categoryNameMap.get(normalizedKey);
        } else {
            return undefined;
        }
    }

    changes: Change[] = [];

    async firstUpdated() {
        if (topicData.topic === undefined) {
            return;
        }
        await pointCache.instantiatePointCache(topicData.topic,topicData.topicKey,true);
        // really we should use allPoints in case there are filters but because we instantiate above we are ok.
        this.hotPoints = createHOTRowsFromPoints(pointCache.points);
        this.refresh();

        // // Register callout for video tutorial button
        // await registerCalloutAfterUpdate({
        //     id: CalloutId.SpreadsheetVideoTutorial,
        //     targetSelector: '#video-tutorial-btn',
        //     type: 'tooltip',
        //     content: 'Adding data in bulk can be tricky! We recommend watching this short instructional video to get started.',
        //     priority: 75,
        //     position: 'bottom'
        // }, this);
    }


    highlightPotentialDuplicates() {
        // get all points from cache
        this.findingDuplicates = true;
        this.showDuplicateButton = false;
        const point_ids = pointCache.getDuplicates();
        const hot = this.handsontableElement.hot;
        const id_column_number = hot.propToCol('id');
        const ids = hot.getDataAtCol(id_column_number);
        // remove last row b/c there's always an extra
        ids.pop();

        // create a data array of true/false values for each row in the tab;e to later be inserted into the table
        const duplicateColumn = hot.getDataAtCol(id_column_number)
            .map((id) => {
                return [point_ids.includes(id) ? true : undefined];
            }
        );

        const colHeaders = hot.getSettings().colHeaders;
        if (Array.isArray(colHeaders) && colHeaders[0] !== 'Potential Duplicate') {
            this.addDuplicateColumn(hot);
        }

        // remove last row from duplicateColumn
        hot.populateFromArray(0, 0, duplicateColumn);
        hot.render();

        this.findingDuplicates = false;
    }


    addDuplicateColumn(hot: HandsontableCore) {
        const columns = hot.getSettings().columns;
        const columnHeaders = hot.getSettings().colHeaders;

        if (Array.isArray(columns) && Array.isArray(columnHeaders)) {
            hot.updateSettings({
                columns: [
                    {
                        readOnly: false,
                    },
                    ...columns,
                ],
                colHeaders: [
                    'Potential Duplicate',
                    ...columnHeaders,
                ],
                hiddenColumns: {
                    columns: [columns.length],
                },
            });
        }
    }

    // Refreshes the map with new data.
    refresh() {

        // Zero out any changes (after a save and update, and reopen, for example).
        this.changes = [];

        // Map the points into the data objects that HOT will consume.
        const data = this.hotPoints.map((p) => {
            const customFields = topicData.topic?.add_point_customization?.custom_fields?.filter((f) => !f.archived) ?? [];
            const customFieldsMap: { [key: string]: any } = {}; // Define the type of customFieldsMap
            customFields.forEach((field) => {
                customFieldsMap[field.customizations?.header ?? `custom_${field._id?.$oid ?? ''}`] = p.custom_information?.custom_field_entries?.find((e) => e.field_id.$oid === field._id?.$oid)?.field_value;
            });

            return {
                id: p._id.$oid,
                email: p.email,
                categoryString: p.topic_category_id === undefined ? '' : this.categoryIdMap.get(p.topic_category_id.$oid)?.name,
                location: p.location,
                locationString: p.location === undefined ? '' : p.location.search,
                name: p.name,
                description: p.description,
                userTagsString: p.tag_info?.user_tags.map(t => t.name).join(','),
                newImageUrls: p.url_images?.map((i) => i.url).join(',') ?? '',
                url: p.url,
                specialOfferDescription: p.special_offer?.description,
                // Show the raw stored UTC ISO so existing paste/CSV
                // workflows keep round-tripping unchanged. The cell
                // renderer adds a local-time subtitle below, so users
                // can see what the value reads as on the ground without
                // changing what's actually pasted/edited/saved.
                startString: p.start,
                endString: p.end,
                createdAtString: p.created_at,
                updatedAtString: p.updated_at,
                numberString: p.number,
                ...customFieldsMap, // Spread the customFieldsMap object
            };
        });

        const colHeaders = [
            'Location *<br> <span style="font-size: smaller;">Full Address or Lat,Long</span>',
            'Category *',
            'Tags<br><span style="font-size: smaller;">(Comma Separated)</span>',
            'Title',
            'Description<br><span style="font-size: smaller;">Plain Text or Markdown</span>',
        ];

        const columns: ColumnSettings[] = [
            {
                data: 'locationString',
                editor: 'point.place',
                validator: getCannotBeEmptyValidator(),
            },
            {
                data: 'categoryString',
                type: 'dropdown',
                renderer: (
                    instance: any,
                    td: any,
                    row: number,
                    _col: number,
                    _prop: any,
                    value: any,
                    _cellProperties: any
                ) => {
                    const category = this.getCategoryFromMap(value);
                    categoryIconRenderer(instance, td, category, row);
                },
                source: topicData.categories.map((c) => c.name),
                validator: getCannotBeEmptyValidator(),
            },
            {
                data: 'userTagsString',
                renderer: (
                    instance: any,
                    td: any,
                    row: number,
                    _col: number,
                    _prop: any,
                    value: any,
                    _cellProperties: any
                ) => {
                    tagRenderer(instance, td, value, row);
                },
                validator: getTagValidator()
            },
            {
                data: 'name',
                validator: getMaxLengthValidator(80),
            },
            {
                data: 'description',
                validator: getMaxLengthValidator(6000),
            }
        ];

        if ((topicData.topic?.has_dates) === true) {
            const has_end = topicData.topic?.has_end_date_time ?? true;
            const custom_header = topicData.topic?.add_point_customization?.date_time?.header ?? 'Date/Time';
            // The topic settings can lock the column to date-only or
            // time-only. That setting otherwise lives on the topic admin
            // page, far from the spreadsheet — surfacing it as a chip in
            // the column header keeps creators from being surprised
            // when their pasted "2026-05-31T19:00Z" loses the time half.
            const dateOnlyMode = topicData.topic?.has_date_only === true;
            const timeOnlyMode = topicData.topic?.has_time_only === true;
            const mode_badge = dateOnlyMode
                ? `<br><span style="display: inline-block; font-size: 0.7em; font-weight: 600; padding: 1px 6px; margin-top: 4px; background: #fff3cd; color: #856404; border-radius: 3px;">Date only — time portion ignored</span>`
                : timeOnlyMode
                    ? `<br><span style="display: inline-block; font-size: 0.7em; font-weight: 600; padding: 1px 6px; margin-top: 4px; background: #d1ecf1; color: #0c5460; border-radius: 3px;">Time only — date portion ignored</span>`
                    : '';
            // Spell out that the column expects UTC ("Zulu") time —
            // local-time entries in YYYY-MM-DDTHH:MM still parse, but
            // they get interpreted as the editor's browser zone, which
            // surprised the customer who pasted a date and saw it
            // shift. Each cell renders the row's local-time equivalent
            // beneath the stored value to confirm the round trip. The
            // format example tracks the mode so it doesn't promise a
            // "T19:00" suffix that gets dropped on save.
            const example = dateOnlyMode
                ? '2026-05-31'
                : timeOnlyMode
                    ? '19:00:00Z'
                    : '2026-05-31T19:00:00Z';
            const format_hint = `<br><span style="font-size: 0.7em; font-weight: 400; color: #666;">UTC / Zulu time<br>e.g. ${example}</span>`;
            colHeaders.push(`${has_end ? `Start ${custom_header}` : custom_header}${mode_badge}${format_hint}`);
            if (has_end) {
                colHeaders.push(`End ${custom_header}${mode_badge}${format_hint}`);
            }
            columns.push(
                {
                    data: 'startString',
                    validator: getDateValidator(),
                    renderer: this.renderDateCell,
                },
            );
            if (has_end) {
                columns.push(
                    {
                        data: 'endString',
                        validator: getDateValidator(),
                        renderer: this.renderDateCell,
                    }
                );
            }
        }

        if ((topicData.topic?.enable_point_numbering) === true) {
            colHeaders.splice(3, 0, 'Point Number');
            columns.splice(3, 0,
                {
                    data: 'numberString',
                    validator: getPointNumberValidator(),
                }
            );
        }

        topicData.topic?.add_point_customization?.custom_fields?.filter(f => !f.archived)?.forEach((field) => {
            const isSingleSelect = 'SingleSelect' in field.field_type;
            const isMultiSelect = 'MultiSelect' in field.field_type;
            const renderer = (instance: any, td: any, row: number, _col: number, _prop: any, value: any, _cellProperties: any) => {
                if ('SingleSelect' in field.field_type ) {
                    singleRenderer(instance, td, field.field_type.SingleSelect.options, value, row);
                } else if ('MultiSelect' in field.field_type) {
                    multiRenderer(instance, td, field.field_type.MultiSelect.options, value, row);
                }
            };
            colHeaders.push(field.customizations?.header ?? `custom_${field._id?.$oid ?? ''}${isMultiSelect ? '<br><span style="font-size: smaller;">(Comma Separated)</span>' : ''}`);
            columns.push({
                data: field.customizations?.header ?? `custom_${field._id?.$oid ?? ''}`,
                validator: getCustomFieldValidator(field),
                type: isSingleSelect ? 'dropdown' : isMultiSelect ? 'autocomplete' : 'text',
                source: isSingleSelect ? 'SingleSelect' in field.field_type ? field.field_type.SingleSelect.options : [] : isMultiSelect ? 'MultiSelect' in field.field_type ? field.field_type.MultiSelect.options : [] : [],
            });
            if (isSingleSelect || isMultiSelect) {
                columns[columns.length - 1].renderer = renderer;
            }
        });

        colHeaders.push('More Info URL','Special Offer','Image URLS to Add<br><span style="font-size: smaller;">(Comma Separated) This will not  <br/> edit or delete existing images.</span>','Point Creator Email','Created At', 'Updated At', 'Proxi Point ID', '');
        columns.push(
            {
                data: 'url',
                validator: getMaxLengthValidator(2000),
            },
            {
                data: 'specialOfferDescription',
                validator: getMaxLengthValidator(1000),
            },
            {
                data: 'newImageUrls',
                type: 'text',
                validator: getMaxLengthValidator(2000),
                renderer: (
                    instance: any,
                    td: any,
                    row: number,
                    _col: number,
                    _prop: any,
                    value: any,
                    _cellProperties: any
                ) => {
                    imageRenderer(instance, td, value, row);
                },
            },
            {
                data: 'email',
                readOnly: true,
            },
            {
                data: 'createdAtString',
                readOnly: true,
            },
            {
                data: 'updatedAtString',
                readOnly: true,
            },
            {
                data: 'id',
                readOnly: true,
            },
            {
                data: 'location',
                readOnly: true,
            }
        );

        const settings = {
            minSpareRows: 1,
            columnSorting: true,
            rowHeaders: true,
            dropdownMenu: ['freeze_column', 'unfreeze_column', '---------', 'filter_by_condition', 'filter_by_value', 'filter_action_bar'],
            filters: true,
            manualColumnResize: true,
            manualColumnFreeze: true,
            overflow: 'hidden',
            width: 1000, // total width of eleemt
            height: 500,
            colHeaders,
            licenseKey: '50d26-41edc-5dd30-3402e-65c5e',
            contextMenu: ['remove_row', 'freeze_column', 'unfreeze_column'],
            startRows: 1,
            fillHandle: {
                autoInsertRow: true,
                direction: 'vertical'
            },
            wordWrap: this.wordWrap,
            autoRowSize: this.wordWrap,
            rowHeights: this.wordWrap
                ? undefined
                : topicData.topic?.has_dates === true ? 44 : 25,
            colWidths: 200,
            hiddenColumns: {
                columns: [columns.length - 1],
            },
            columns,
            // This ensures that, when a new row or rows is pasted in, the data is properly "tracked"
            // as a point in the list of points bound to this table.
            beforeChange: (cs: any[], source: string) => {
                // Structure of each element in cs: `[row, prop, oldVal, newVal]`.
                // Populate from Array is part of adding duplicate column
                if (
                    cs === null ||
                    source === 'loadData' ||
                    source === 'updatePointFromTable' ||
                    source === 'createPlaceEditor' ||
                    source === 'populateFromArray'
                ) {
                    return;
                }

                // Put changes into an easy-to-read object.
                const changes: HotChange[] = cs.map((c) => {
                    return {
                        row: c[0],
                        prop: c[1],
                        oldValue: c[2],
                        newValue: c[3],
                    };
                });

                // Group the changes by row. new ones only
                const newGroupedChanges = changes
                    .filter((c) => this.handsontableElement.hot.getDataAtRowProp(c.row, 'id') === null)
                    .reduce((acc, c) => {
                        if (!acc.has(c.row)) {
                            acc.set(c.row, []);
                        }
                        acc.get(c.row)?.push(c);
                        return acc;
                    }, new Map<number, HotChange[]>());

                const customFields = topicData.topic?.add_point_customization?.custom_fields?.filter(f => !f.archived) ?? [];
                const customFieldHeaders = new Set(customFields.map(field => field.customizations?.header ?? `custom_${field._id?.$oid ?? ''}`));

                // Create a point, and add new "changes" to the list of changes to fill out the defaults
                const locationColumnNumber = this.handsontableElement.hot.propToCol('locationString');
                for (const [row, propertyChanges] of newGroupedChanges) {

                    // These are special cases that are handled by the PlaceEditor.
                    // The PlaceEditor will update these meta values, and we want to make
                    // sure they are captured in the point we are creating on paste.
                    const lastEditLocation = this.handsontableElement.hot.getCellMeta(
                        row,
                        locationColumnNumber
                    ).location as Location | undefined;
                    const lastEditLocationString = this.handsontableElement.hot.getCellMeta(
                        row,
                        locationColumnNumber
                    ).locationString as Location | undefined;
                    if (topicData.topic === undefined) {
                        return;
                    }
                    const newPoint = createDummyPoint(topicData.topic);

                    // Sanitize changes
                    const sanitizedChanges = propertyChanges.filter((c) => isDefined(c.newValue));

                    // Apply changes to the new point
                    for (const change of sanitizedChanges) {
                        if (change.prop === 'categoryString') {
                            newPoint.topic_category_id = this.getCategoryFromMap(change.newValue)?._id;
                        } else if (change.prop === 'locationString') {
                            (newPoint.location as any) = {
                                search: change.newValue ?? lastEditLocationString,
                                latLng: undefined,
                                place_id: undefined,
                            };
                        } else if (change.prop === 'userTagsString') {
                            const tags_as_array: UserTag[] = this.normalizeUserTags(change.newValue.split(','));
                            newPoint.tag_info?.user_tags.push(...tags_as_array);
                        } else if (change.prop === 'newImageUrls') {
                            newPoint.url_images = undefined;
                        }
                        else if (change.prop === 'specialOfferDescription') {
                            newPoint.special_offer = {
                                description: change.newValue,
                            };
                        } else if (change.prop === 'startString') {
                            getMainPage().errorToastElement.tryOrErrorToastSync(() => {
                                newPoint.start = new Date(change.newValue).toISOString();
                            }, 'Start date is invalid.');
                        } else if (change.prop === 'endString') {
                            getMainPage().errorToastElement.tryOrErrorToastSync(() => {
                                newPoint.end = new Date(change.newValue).toISOString();
                            }, 'End date is invalid.');
                        } else if (change.prop === 'numberString') {
                            getMainPage().errorToastElement.tryOrErrorToastSync(() => {
                                newPoint.number = change.newValue;
                            }, 'Point number is invalid.');
                        } else  if (customFieldHeaders.has(change.prop)) {
                            // Find the field definition
                            const field = customFields.find(f => (f.customizations?.header ?? `custom_${f._id?.$oid ?? ''}`) === change.prop);
                            if (field?._id !== undefined && isDefined(undefinedIfEmpty(change.newValue))) {
                                // Ensure custom_information exists
                                if (!newPoint.custom_information) {
                                    newPoint.custom_information = {
                                        passthrough_parameters: undefined,
                                        custom_field_entries: [],
                                    };
                                }
                                // Find or create the entry
                                const entries = newPoint.custom_information.custom_field_entries;
                                const existing = entries?.find(e => e.field_id.$oid === field._id?.$oid);
                                if (existing) {
                                    existing.field_value = change.newValue;
                                } else {
                                    entries?.push({
                                        field_id: field._id,
                                        field_value: change.newValue,
                                    });
                                }
                            }
                        } else {
                            // Handle other props as before
                            (newPoint as any)[change.prop] = change.newValue;
                        }
                    }

                    if (lastEditLocation !== undefined) {
                        newPoint.location = lastEditLocation;
                    }

                    // Update the table with the new point's data
                    cs.push([row, 'id', null, newPoint._id.$oid]);
                    cs.push([row, 'location', null, newPoint.location]);
                    cs.push([row, 'email', null, newPoint.email]);
                    cs.push([row, 'locationString', null, newPoint.location?.search]);
                    cs.push([row, 'categoryString', null, this.categoryIdMap.get(newPoint.topic_category_id?.$oid ?? '')?.name]);
                    cs.push([row, 'name', null, newPoint.name]);
                    cs.push([row, 'description', null, newPoint.description]);
                    cs.push([row, 'userTagsString', null, newPoint.tag_info?.user_tags.map(tag => tag.name).join(',')]);
                    cs.push([row, 'newImageUrls', null, newPoint.url_images?.map((i) => i.url).join(',') ?? '']);
                    cs.push([row, 'url', null, newPoint.url]);
                    cs.push([row, 'specialOfferDescription', null, newPoint.special_offer?.description]);

                    if (topicData.topic?.has_dates) {
                        cs.push([row, 'startString', null, newPoint.start]);
                        cs.push([row, 'endString', null, newPoint.end]);
                    }

                    if (topicData.topic?.enable_point_numbering === true) {
                        cs.splice(5, 0, [row, 'numberString', null, newPoint.number]);
                    }
                    topicData.topic?.add_point_customization?.custom_fields?.filter(f => !f.archived)?.forEach((field) => {
                        // look to see if the field is in the custom fields
                        cs.push([row, field.customizations?.header ?? `custom_${field._id?.$oid ?? ''}`, null, newPoint.custom_information?.custom_field_entries?.find((e) => e.field_id.$oid === field._id?.$oid)?.field_value]);
                    });

                    // Add the new point to the list of points
                    newPoint.is_new = true;
                    this.hotPoints.push(newPoint);
                }
            },

            // This is where we compute any changes to the table when a row is added or edited.
            afterChange: (cs: any[], source: any) => {
                // Structure of each element in cs: `[row, prop, oldVal, newVal]`.
                // Populate from Array is part of adding duplicate column
                if (
                    cs === null ||
                    source === 'loadData' ||
                    source === 'updatePointFromTable' ||
                    source === 'createPlaceEditor' ||
                    source === 'populateFromArray'
                ) {
                    return;
                }

                // Put changes into an easy-to-read object.
                const changes: HotChange[] = cs.map((c) => {
                    return {
                        row: c[0],
                        prop: c[1],
                        oldValue: c[2],
                        newValue: c[3],
                    };
                });

                // Group the changes by row.
                const groupedChanges = changes
                    .filter((c) => c.oldValue !== c.newValue)
                    .reduce((acc, c) => {
                        if (!acc.has(c.row)) {
                            acc.set(c.row, []);
                        }

                        acc.get(c.row)?.push(c);

                        return acc;
                    }, new Map<number, HotChange[]>());

                // Update the point with all of the changes for each row.
                const newChanges = Array.from(groupedChanges.values()).map((c) =>
                    this.updatePointFromTable(c)
                );

                // Merge the changed points into the `changes` array.
                this.mergeChangedPoints(this.changes, newChanges);
            },

            // This is how we capture row deletion.
            beforeRemoveRow: (index: number, amount: number) => {
                const newChanges = [];

                for (let row = index; row < index + amount; row++) {
                    const id = this.handsontableElement.hot.getDataAtRowProp(row, 'id');

                    newChanges.push({
                        id,
                        changeType: ChangeType.Delete,
                        point: undefined,
                    });
                }

                // Merge the changed points into the `changes` array.
                this.mergeChangedPoints(this.changes, newChanges);
            },
        };
        this.handsontableElement.settings = settings;
        // trying settings before data to set the size before we load the data
        this.handsontableElement.data = data;
    }

    // This method sort of does what it says, but the logic is very precise, so we need
    // to be careful when changing it.
    protected updatePointFromTable(changes: HotChange[]) {
        const row = changes[0].row;
        const id = this.handsontableElement.hot.getDataAtRowProp(row, 'id') as string;

        const point = this.hotPoints.find((p) => p._id.$oid === id);

        // This should never happen: at this point, the point should have been added into
        // the `this.hotPoints` array, or it was already there to begin with.
        if (point === undefined) {
            throw new Error('Could not find the point with id ' + id);
        }

        let changeType;

        // These are special cases that are handled by the PlaceEditor.
        // The PlaceEditor will update these meta values, and we want to make
        // sure they are captured in the point we are creating on paste.
        const locationColumnNumber = this.handsontableElement.hot.propToCol('locationString');
        const lastEditLocation = this.handsontableElement.hot.getCellMeta(
            row,
            locationColumnNumber
        ).location as Location | undefined;
        const lastEditLocationString = this.handsontableElement.hot.getCellMeta(
            row,
            locationColumnNumber
        ).locationString as Location | undefined;

        if (point.is_new ?? false) {
            // If this is a "new" point, we need to mark it as `Create` here.
            changeType = ChangeType.Create;
            point.is_new = undefined;
        } else {
            // Otherwise, find the point and get field values.
            changeType = ChangeType.Update;

            // Get all of the values from the table.
            const email = this.handsontableElement.hot.getDataAtRowProp(row, 'email');
            const categoryString = this.handsontableElement.hot.getDataAtRowProp(
                row,
                'categoryString'
            );
            const locationString =
                lastEditLocationString ??
                this.handsontableElement.hot.getDataAtRowProp(row, 'locationString');
            let location =
                lastEditLocation ?? this.handsontableElement.hot.getDataAtRowProp(row, 'location');
            const name = this.handsontableElement.hot.getDataAtRowProp(row, 'name');
            let description = this.handsontableElement.hot.getDataAtRowProp(row, 'description');
            if (isValidString(description)) {
                description = new DOMParser().parseFromString(description, 'text/html').documentElement.textContent ?? '';
            }
            const userTagsString = this.handsontableElement.hot.getDataAtRowProp(
                row,
                'userTagsString'
            ) as string ?? '' as string;
            const newImagesString = this.handsontableElement.hot.getDataAtRowProp(
                row,
                'newImageUrls'
            ) as string ?? '' as string;
            const imagesToAdd: uploadImageUrlRequest[] = newImagesString
                .split(',')
                .map((url) => url.trim())
                .filter((url) => url !== '')
                .map((url) => {
                    return {
                        name: 'custom',
                        url,
                    };
                });
            const url = this.handsontableElement.hot.getDataAtRowProp(row, 'url');
            const specialOfferDescription = this.handsontableElement.hot.getDataAtRowProp(
                row,
                'specialOfferDescription'
            );
            const startString = this.handsontableElement.hot.getDataAtRowProp(row, 'startString');
            const endString = this.handsontableElement.hot.getDataAtRowProp(row, 'endString');
            const numberString = this.handsontableElement.hot.getDataAtRowProp(row, 'numberString');

            // Update the point object's fields to match any changes made in the table.

            const category = topicData.categories.find((c) => c.name.toLowerCase() === categoryString?.toLowerCase());
            const user_tags = userTagsString
                .split(',')
                .map((t) => t.trim())
                .filter((t) => t !== '');

            if (locationString !== location.search) {
                location = {
                    search: locationString,
                    latLng: undefined,
                    place_id: undefined,
                };
            }

            let start;
            let end;
            if (topicData.topic?.has_dates === true) {
                // This can fail, so put in a try.
                getMainPage().errorToastElement.tryOrErrorToastSync(() => {
                    start = undefinedIfEmpty(startString) === undefined
                            ? undefined
                            : new Date(startString).toISOString();
                }, 'Start date is invalid.');

                getMainPage().errorToastElement.tryOrErrorToastSync(() => {
                    end = undefinedIfEmpty(endString) === undefined
                            ? undefined
                            : new Date(endString).toISOString();
                }, 'End date is invalid.');
            }

            point.email = email;
            point.topic_category_id = category?._id;
            point.location = location;
            point.name = undefinedIfEmpty(name);
            point.description = undefinedIfEmpty(description);
            point.tag_info = {
                user_tags: this.normalizeUserTags(user_tags),
                google_tags: point.tag_info?.google_tags ?? [],
            };
            point.url_images = imagesToAdd;
            point.url = undefinedIfEmpty(url);
            point.special_offer = isEmpty(specialOfferDescription)
                ? undefined
                : { description: specialOfferDescription };
            point.start = start;
            point.end = end;
            point.number = undefinedIfEmpty(numberString);

            // Update custom fields
            const customFields = topicData.topic?.add_point_customization?.custom_fields?.filter(f => !f.archived) ?? [];
            customFields.forEach((field) => {
                if (field._id === undefined) {
                    return;
                }
                const value = this.handsontableElement.hot.getDataAtRowProp(row, field.customizations?.header ?? `custom_${field._id?.$oid ?? ''}`);
                if (!isDefined(undefinedIfEmpty(value))) {
                    // Cell was cleared — drop any stale entry so the old value doesn't ride along on save
                    if (point.custom_information?.custom_field_entries !== undefined) {
                        point.custom_information.custom_field_entries = point.custom_information.custom_field_entries.filter(
                            (e) => e.field_id.$oid !== field._id?.$oid,
                        );
                    }
                    return;
                }
                if (point.custom_information?.custom_field_entries === undefined) {
                    point.custom_information = {
                        passthrough_parameters: point.custom_information?.passthrough_parameters,
                        custom_field_entries: [{ field_id: field._id, field_value: value }],
                    };
                } else {
                    const existing = point.custom_information.custom_field_entries.find((e) => e.field_id.$oid === field._id?.$oid);
                    if (existing !== undefined) {
                        existing.field_value = value;
                    } else {
                        point.custom_information.custom_field_entries.push({ field_id: field._id, field_value: value });
                    }
                }
            });
        }

        return {
            id: point._id.$oid,
            changeType,
            point,
        };
    }

    private normalizeUserTags(userTagString: string[]): UserTag[] {
        return topicData.allUserTags?.filter(tag =>
            userTagString.map(ut => ut.toLowerCase().trim()).includes(tag.name.toLowerCase().trim())
        ) ?? [];
    }

    protected tableReady() {
        Handsontable.editors.registerEditor('point.place', createPlaceEditor());
    }

    private toggleWordWrap() {
        this.wordWrap = !this.wordWrap;
        this.handsontableElement.hot.updateSettings({
            wordWrap: this.wordWrap,
            autoRowSize: this.wordWrap,
            rowHeights: this.wordWrap
                ? undefined
                : topicData.topic?.has_dates === true ? 44 : 25,
        });
    }

    // Merges two sets of changes into one set of new changes.
    private mergeChangedPoints(target: Change[], source: Change[]) {
        for (const newChange of source) {
            let existingChange = target.find((c) => c.id === newChange.id);

            // First, remove any other changes if this is a delete.
            if (newChange.changeType === ChangeType.Delete && existingChange !== undefined) {
                target.splice(target.indexOf(existingChange), 1);

                // If we removed a "create", then we're done, otherwise, we need to add a delete.
                if (existingChange.changeType === ChangeType.Create) {
                    continue;
                } else {
                    existingChange = undefined;
                }
            }

            // If there is no existing change for the point, add it to the list: the change type doesn't matter.
            // This covers create, update, and delete.
            // Otherwise, if this is an update, merge the changes into the existing change.
            if (existingChange === undefined) {
                target.push(newChange);
            } else if (newChange.changeType === ChangeType.Update) {
                if (existingChange.point === undefined || newChange.point === undefined) {
                    throw new Error('Undefined existing change or new change');
                }
                Object.assign(existingChange.point, newChange.point);
            } else {
                throw new Error('Invalid change state.');
            }
        }
    }

    // Saves the changes to the server.
    protected async save() {

        // Set html to upload
        this.uploadInProgress = true;

        const pointsToAdd: CreatePointRequest[] = [];
        const pointsToUpdate: CreatePointRequest[] = [];
        const pointsToDelete: string[] = [];

        // Separate the changes into the three types.
        for (const changedPoint of this.changes) {
            if (changedPoint.changeType === ChangeType.Delete) {
                pointsToDelete.push(changedPoint.id);
                continue;
            }

            // This should never happen.
            if (changedPoint.point === undefined) {
                this.uploadInProgress = false;
                throw new Error('Point is undefined for create or update.');
            }

            if (changedPoint.changeType === ChangeType.Create) {
                // [ARoney] A few things need to be hacked around so the server can figure out
                // how to handle things properly.
                changedPoint.point.email = topicData.email ?? '';
                let pointRequest: CreatePointRequest;
                try {
                    pointRequest = pointRequestFromHOTRow(changedPoint.point);
                } catch (e) {
                    this.uploadInProgress = false;
                    throw new Error('Error in Creating Create Point Request');
                }

                const newPoint = {
                    ...pointRequest,
                    _id: undefined,
                };

                if (newPoint.location?.latLng === undefined) {
                    // check if input looks like lat/long
                    const locationString = newPoint.location?.search;
                    if (locationString !== undefined) {
                        const isLatLongNew = await isLocationInputLatLong(locationString);

                        if (isLatLongNew !== false) {
                            const latLongInput: Location = {
                                search: locationString,
                                place_id: 'custom',
                                latLng: isLatLongNew,
                            };
                            newPoint.location = latLongInput;
                        }
                    }
                }
                pointsToAdd.push(newPoint);
            } else if (changedPoint.changeType === ChangeType.Update) {
                let pointRequest: CreatePointRequest;
                try {
                    pointRequest = pointRequestFromHOTRow(changedPoint.point);
                } catch (e) {
                    this.uploadInProgress = false;
                    throw new Error('Error in Creating Update Create Point Request');
                }

                // check if input looks like lat/long
                if (pointRequest.location?.latLng === undefined) {
                    if (pointRequest?.location?.search) {
                        const locationString = pointRequest.location.search;
                        const isLatLongUpdate = await isLocationInputLatLong(locationString);

                        if (isLatLongUpdate !== false) {
                            const latLongInput: Location = {
                                search: locationString,
                                place_id: 'custom',
                                latLng: isLatLongUpdate,
                            };
                            pointRequest.location = latLongInput;
                        }
                    }
                }
                pointsToUpdate.push(pointRequest);
            } else {
                this.uploadInProgress = false;
                getMainPage().errorToastElement.showToast('Error validating points. Please ensure all required fields (location and category) are present.');
            }
        }

        // validate that they have access to add more points
        if (
            pointCache.allPoints.length + pointsToAdd.length - pointsToDelete.length >= featuresInfo.pointsPerMapEnabled
        ) {
            this.uploadInProgress = false;
            getMainPage().errorToastElement.showToastAndThrow(
                'You have reached the maximum number of points allowed on this map.  Please upgrade to add more.'
            );
            return;
        }

        // tell them that we are starting the upload
        getMainPage().errorToastElement.showToast(`Points Validated, starting upload. ${pointsToAdd.length} points to add, ${pointsToUpdate.length} points to update, ${pointsToDelete.length} points to delete.`);
        const delete_results: Array<{ _id: string, error: string }> = [];
        let delete_successes: number = 0;
        let process_message: string = '';
        try {
            // [ARoney] The delete is a set of tasks because it is _usually_ frowned upon to have a "bulk delete" REST operation.
            // We can add one later, if needed.
            const addChunks: CreatePointRequest[][] = chunk(pointsToAdd, 25);
            const addTask = (async () => {
                const addTaskResult: CreatePointsResponse =  {
                    failed_geocodes: [],
                    failed_validations: [],
                    updated_point_ids: [],
                };
                for (let i = 0; i < addChunks.length; i++) {
                    // Add delay before each chunk
                    const result = await addPoints(
                        HotPointsElement.amplitude_callsite,
                        topicData.topicId,
                        addChunks[i],
                        topicData.topicKey
                    );
                    addTaskResult.failed_geocodes.push(...result.failed_geocodes);
                    addTaskResult.failed_validations.push(...result.failed_validations);
                    addTaskResult.updated_point_ids.push(...result.updated_point_ids);
                    getMainPage().errorToastElement.showToast(`${addTaskResult.updated_point_ids.length} points added. Continuing...`);
                    if (i < addChunks.length - 1) {
                        await delay(20);
                    }
                }
                return addTaskResult;
            })();

            const updateChunks: CreatePointRequest[][] = chunk(pointsToUpdate, 25);
            const updateTask = (async () => {
                const updateTaskResult: CreatePointsResponse = {
                    failed_geocodes: [],
                    failed_validations: [],
                    updated_point_ids: [],
                };
                for (let i = 0; i < updateChunks.length; i++) {
                    // Add delay before each chunk
                    const result = await updatePoints(
                        HotPointsElement.amplitude_callsite,
                        topicData.topicId,
                        updateChunks[i],
                        topicData.topicKey
                    );
                    updateTaskResult.failed_geocodes.push(...result.failed_geocodes);
                    updateTaskResult.failed_validations.push(...result.failed_validations);
                    updateTaskResult.updated_point_ids.push(...result.updated_point_ids);
                    getMainPage().errorToastElement.showToast(`${updateTaskResult.updated_point_ids.length} points updated. Continuing...`);
                    if (i < updateChunks.length - 1) {
                        await delay(20);
                    }
                }
                return updateTaskResult;
            })();

            const deleteChunks = chunk(pointsToDelete, 5);
            const deleteTasks = deleteChunks.map(async (chunk, i) => {
                // Add delay before each chunk
                await delay(i);

                for (const id of chunk) {
                    try {
                        await deletePoint(
                            HotPointsElement.amplitude_callsite,
                            topicData.topicId,
                            id,
                            topicData.topicKey
                        );
                        delete_successes++;
                    } catch (e) {
                        delete_results.push({ _id: id, error: 'Could Not Delete' });
                    }
                }
                getMainPage().errorToastElement.showToast(`${delete_successes} points deleted. Continuing...`);
            });
            // We used to do these together but now we're separating to make the errors more clear.
            const results: CreatePointsResponse[] = [];

            if (pointsToAdd.length > 0) {
                try {
                    const add_task_result = await addTask;
                    if (add_task_result.updated_point_ids.length > 0) {
                        process_message += `Successfully Added ${add_task_result.updated_point_ids.length} New Points, `;

                    } else if (add_task_result.failed_geocodes.length > 0 || add_task_result.failed_validations.length > 0) {
                        process_message += `Failed to Geocode ${add_task_result.failed_geocodes.length + add_task_result.failed_validations.length} New Points`;
                    }
                    results.push(add_task_result);
                } catch (e) {
                    process_message += 'Server Error When Adding New Points, Please Reach out to Proxi for Assistance, ';
                }
            }

            if (pointsToUpdate.length > 0) {
                try {
                    const update_task_result = await updateTask;
                    if (update_task_result.updated_point_ids.length > 0) {
                        process_message += `Successfully Updated ${update_task_result.updated_point_ids.length} Points,  `;
                        // invalidate the cache for the updated points in case images were updated
                        try {
                            for (const point_id of update_task_result.updated_point_ids) {
                                invalidatePointImagesCache(topicData.topicId, point_id);
                            }
                        } catch (e) {
                            console.error('Error invalidating point images cache for updated points', e);
                        }
                    } else if (update_task_result.failed_geocodes.length > 0 || update_task_result.failed_validations.length > 0) {
                        process_message += `Failed to Geocode ${update_task_result.failed_geocodes.length + update_task_result.failed_validations.length} Updated Points, `;
                    }
                    results.push(update_task_result);
                } catch (e) {
                    process_message += 'Server Error When Updating Points, Please Reach out to Proxi for Assistance, ';
                }
            }

            await Promise.all(deleteTasks);
            if (delete_successes > 0) {
                process_message += `Successfully Deleted ${delete_successes} Points (you may need to refresh the page), `;
            } else if (delete_results.length > 0) {
                process_message += `Failed to Delete ${delete_results.length} Points, `;
            }

            const errorString = createErrorStringForBulk(results, topicData.topic, this.categoryIdMap, delete_results);
            if (errorString !== undefined) {
                getMainPage().downloadableErrorToastElement.showDownloadableToast(window.URL.createObjectURL(new Blob([errorString], {type: 'text/csv'})), `${process_message}. Download this CSV to see error details. Please refresh your browser before retrying`, errorString.split('\n').length - 1);
            } else if (process_message !== '') {
                getMainPage().downloadableErrorToastElement.showDownloadableToast('', process_message, 0);
            }
        } catch (e) {
            this.uploadInProgress = false;
            getMainPage().errorToastElement.showToast('Error contacting the server.');
            throw e;
        }

        // Since the change was successful, zero out the changes.
        this.changes = [];
        this.uploadInProgress = false;

        this.dispatchEvent(new CustomEvent('on-save'));
    }

    close() {
        if (!confirm('Are you sure you want to close the spreadsheet view? Any unsaved changes will be lost.')) {
            return;
        }
        this.dispatchEvent(new CustomEvent('on-close'));
    }

    async onUpgradeClick() {
        const addon = this.getCustomFormAddOn();
        if (addon === undefined || addon === null) {
            getMainPage().errorToastElement.showToast('Could not find the Crowdsourcing & Field Customization Add On. Please try again later.');
            return;
        }
        await topicData.installAddon(HotPointsElement.amplitude_callsite, addon);
    }

    getCustomFormAddOn() {
        return topicData.allAddons.find((a) => a.component === AddOnType.CustomForm);
    }

    hasCustomFormAddon() {
        const addon = this.getCustomFormAddOn();
        return addon !== undefined && topicData.activatedAddons.includes(addon);
    }

    render() {
        return html`
            <div class="hot-points">
                <span class="hot-points__header">
                    <div style="flex:1">
                        <h3 style="margin: 0">Spreadsheet View - Bulk Add, Edit, and Paste Map Points</h3>
                        <span>You can copy and paste up to 1000 rows at a time.
                            ${this.hasCustomFormAddon() ? '' : html`
                                <br>
                                <b>
                                Want to edit columns and add custom ones? <a @click="${this.onUpgradeClick}" style="color: #007bff; text-decoration: underline; cursor: pointer;">Try The Crowdsourcing & Field Customization Add On</a>
                            `}
                        <div class="helper_buttons">
                            <!-- <form-button id="video-tutorial-btn" class="help-btn" variant="outline"
                                Watch Video Tutorial
                            </form-button> -->
                            ${this.showDuplicateButton ? html`
                                ${this.findingDuplicates ? html`
                                <loading-element loadingString="Finding Duplicates..."></loading-element>
                                ` : html`
                                <form-button class="help-btn" variant="outline" @click="${this.highlightPotentialDuplicates}">
                                    Highlight Potential Duplicates (can take up to a minute)
                                </form-button>
                                `}
                            ` : ''}
                            <form-button class="help-btn" variant="outline" @click="${() => pointCache.onDownloadCsv(true)}">
                                Download Point Data
                            </form-button>
                            <form-button class="help-btn" variant="outline" @click="${this.toggleWordWrap}">
                                ${this.wordWrap ? 'Disable' : 'Enable'} Word Wrap
                            </form-button>
                        </div>
                        ${topicData.challengeHappeningNow ? html`
                            <div class="challengewarning" style="display: flex; align-items: center; gap: 5px; margin-top: 0.5rem; color: var(--main-invalid-color);">
                                <mwc-icon>warning</mwc-icon>
                                <span>There is a challenge happening right now. Changing the <b>locations</b> of points will impact check-in codes and users' challenge progress.</span>
                            </div>
                        ` : ''}
                    </div>
                    <span style="display: flex; flex-direction: row; align-items: center; gap: 8px;">
                        <form-button variant="outline" @click="${this.close}">Cancel</form-button>
                        <form-button @click="${this.save}">Save and load map</form-button>
                    </span>
                </span>
                ${this.uploadInProgress ? html`
                    <div class="resultDialogContainer">
                        <div>Updating Points...</div>
                        <vaadin-progress-bar indeterminate></vaadin-progress-bar>
                    </div>
                    <error-toast id="errorToast"></error-toast>
                `: ''}
                <div class="hot-points__holder">
                    <handsontable-element
                        id="handsontable"
                        class="hot-points__spreadsheet"
                        @ready="${this.tableReady}"
                    ></handsontable-element>
                </div>
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    static get styles() {
        return css`
            .hot-points {
                background-color: white;
                height: 100%;
            }

            .hot-points__header {
                display: flex;
                padding: 8px;
                justify-content: space-between;
            }

            .hot-points__holder {
                padding: 8px;
                /**height of page minus all the top stuff this dictates the HOT heisght */
                height: calc(100vh - 250px);
                display: flex;
            }

            .hot-points__btn {
                padding-top: 4px;
                padding-left: 8px;
            }

            .helper_buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 8px;
                margin-top: 8px;
            }

            paper-input-place {
                --paper-font-caption_-_line-height: 0;
            }
        `;
    }
}

function createPlaceEditor() {
    abstract class PlaceEditor extends Handsontable.editors.TextEditor {
        placeElement: any & HTMLInputElement;
        _opened: boolean = false;

        init() {
            // Create detached node, add CSS class and make sure it's not visible.
            this.placeElement = this.hot.rootDocument.createElement('proxi-input-place');
            this.placeElement.style.display = 'none';
            this.placeElement.style.position = 'absolute';
            this.placeElement.addEventListener('focus', async () => {
                await removeInertFromLocationElement();
            });

            // Attach node to DOM by appending it to the container holding the table.
            this.hot.rootElement.appendChild(this.placeElement);
        }

        // Called when HOT is trying to ask the editor what the most recent value was.
        // I.e., whatever it was after a change.
        getValue() {
            return this.placeElement.value?.search;
        }

        // Called when the editor is first opened so HOT can tell us what to put in the editor.
        setValue(_value: string) {
            // If there was a recent update, it will be in the metadata; otherwise, use the value from the `location` property.
            this.updatePlaceElementValue(_value, this.row);
        }

        updatePlaceElementValue(value: string | Location | undefined, row: number) {
            const newValue = value ?? this.hot.getDataAtRowProp(row, 'location');
            if (typeof newValue === 'string') {
                this.placeElement.value = {
                    search: newValue,
                    latLng: undefined,
                    place_id: undefined,
                };
            } else {
                this.placeElement.value = newValue;
            }
        }

        focus() {
            this.placeElement.focus();
        }

        // Called when the editor is opened and needs to be laid out.
        open() {
            const { top, start, width, height } = (this as any).getEditedCellRect();

            const style = this.placeElement.style;

            this._opened = true;

            // Movers the element into place.
            style.height = `${+height}px`;
            style.width = `${+width}px`;
            style.top = `${+top}px`;
            style[this.hot.isRtl() ? 'right' : 'left'] = `${+start}px`;
            style.margin = '0px';
            style.display = '';
            style.background = 'white';

            this.placeElement.mini = true;

            // Captures certain events that we need to handle properly (e.g., when the user hits ENTER).
            this.hot.addHook('beforeKeyDown', this.beforeKeyDown);
            this.placeElement.addEventListener('value-changed', this.valueChanged);

            // Ensures that the editor is ready to accept input,
            // and any dropdowns will be open.
            setTimeout(async () => {
                await this.focusHelper();
            }, 10);
        }

        // Called when the editor is closed.
        close() {
            // Cleanup CSS and helpers.
            this._opened = false;
            this.placeElement.style.display = 'none';

            // Deslect the cell.
            this.hot.deselectCell();

            // Ensure that "global"-ish events are removed.
            this.hot.removeHook('beforeKeyDown', this.beforeKeyDown);
            this.placeElement.removeEventListener('value-changed', this.valueChanged);
        }

        // Called while HOT is completing editing.  This is a tad tricky because we need to
        // sometimes _not_ finish editing if this was called because a user clicked the
        // location dropdown; hence, the need to check for any open ones, and block the continuation
        // otherwise.
        finishEditing(
            restoreOriginalValue?: boolean | undefined,
            ctrlDown?: boolean | undefined,
            _callback?: (() => void) | undefined
        ) {
            // Ensures that a "click" event which invokes `finishEditing` is not propagated if the PAC is open.
            if (isAnyPacContainerOpen()) {
                return;
            }

            super.finishEditing(restoreOriginalValue, ctrlDown, () => {
                if (this._opened) {
                    this.close();
                }
            });
        }

        // [ARoney] https://i.kym-cdn.com/entries/icons/original/000/000/063/Rage.jpg
        protected async focusHelper() {
            await delay(0);
            this.placeElement.focus();
            await delay(0);
            this.placeElement.blur();
            await delay(0);
            this.placeElement.focus();
        }

        // Events that attempt to ensure ENTER and TAB work properly.
        // In addition, ensures that other keys make sense; for example,
        // arrow keys will move the curssor inside the editor, rather than
        // change cells.
        beforeKeyDown = this.beforeKeyDownInner.bind(this);
        beforeKeyDownInner(event: KeyboardEvent) {
            if (event.keyCode === Handsontable.helper.KEY_CODES.TAB) {
                return;
            }

            if (event.keyCode === Handsontable.helper.KEY_CODES.ENTER) {
                return;
            }

            Handsontable.dom.stopImmediatePropagation(event);
        }

        // Any time the place element changes, we need to ensure the mose up-to-date value
        // is placed in the metadata for the cell.  We use metadata because setting actual
        // hidden cell values triggers additional updates, which we want to avoid.
        valueChanged = this.valueChangedInner.bind(this);
        valueChangedInner(_event: KeyboardEvent) {
            const locationColumnNumber = this.hot.propToCol('locationString');
            this.hot.setCellMeta(
                this.row,
                locationColumnNumber,
                'location',
                this.placeElement.value
            );
            this.hot.setCellMeta(
                this.row,
                locationColumnNumber,
                'locationString',
                this.placeElement.value.search
            );
        }
    }

    return PlaceEditor;
}

// Renders the category icon for the category.
function categoryIconRenderer(instance: any, td: HTMLTableCellElement, category: TopicCategory | undefined, row: number) {
    // If category is not found, highlight red. Exclude any rows that have no data (i.e. the last row)
    if (category === undefined) {
        td.innerHTML = '';
        let isEmptyRow = true;
        const rowData = (instance as HandsontableCore).getDataAtRow(row);
        for (let i = 0; i < rowData.length; i++) {
            if (rowData[i] !== null && (rowData[i] as Location)?.search !== null) {
                isEmptyRow = false;
            }
        }
        if (!isEmptyRow) {
            td.style.backgroundColor = '#ff4c44'; // HOT error color
        }
    } else {
        td.innerHTML = `<img width="18" height="18" style="vertical-align: text-top;" src="${getCategoryIcon(category._id)}"></img> ${category.name}`;
    }
}


// Renders the image urls inline as images with a small size
function imageRenderer(instance: any, td: HTMLTableCellElement, imageUrls: string | undefined, row: number) {
    // put comma separated tags into arrat
    const imageUrlsArray = imageUrls?.split(',')
                .map((t) => t.trim())
                .filter((t) => t !== '') ?? [];
    if (!isDefined(imageUrlsArray) || imageUrlsArray.length === 0) {
        td.innerHTML = '';
    } else {
        let htmlContent = '';
        imageUrlsArray.forEach(url => {
            htmlContent += `<img src="${url}" style="max-height: 25px; max-width: 50px; margin-right: 5px;" />`;
        });
        td.innerHTML = htmlContent;
    }
}

// Renders the tags inline as pills with the appropriate color
function tagRenderer(instance: any, td: HTMLTableCellElement, tags: string | undefined, row: number) {
    // put comma separated tags into arrat
    const tags_array = tags?.split(',')
                .map((t) => t.trim())
                .filter((t) => t !== '') ?? [];
    if (!isDefined(tags_array) || tags_array.length === 0) {
        td.innerHTML = '';
    } else {
        let htmlContent = '';
        tags_array.forEach(tag => {
            const foundTag = topicData.allUserTags?.find(t => t.name.trim().toLowerCase() === tag.trim().toLowerCase());
            if (foundTag) {
                // Valid tag styling
                htmlContent += `<span class="tag-pill" style="background-color: ${foundTag.icon}; color: ${getVisibleTextColor(foundTag.icon)}; padding: 2px 5px; border-radius: 2px; padding-top: 2px;">${foundTag.name}</span> `;
            } else {
                // Invalid tag styling
                htmlContent += `<span class="tag-pill invalid-tag" color: #white; padding: 2px 5px; border-radius: 2px; padding-top: 2px;"><img width="18" height="18" style="vertical-align: text-top;" src="https://img.icons8.com/?size=48&id=KarJz0n4bZSj&format=png"></img>${tag}</span> `;
                td.style.backgroundColor = '#ff4c44';
            }
        });
        td.innerHTML = htmlContent;
    }
}

// Renders the mutli-select dropdown options
function multiRenderer(instance: any, td: HTMLTableCellElement, available_options: string[] | undefined, selected_options: string | undefined, row: number) {
    // put comma separated tags into arrat
    const selected_options_array = selected_options?.split(',')
                .map((t) => t.trim())
                .filter((t) => t !== '') ?? [];
    if (!isDefined(selected_options_array) || selected_options_array.length === 0) {
        td.innerHTML = '';
    } else {
        let htmlContent = '';
        selected_options_array.forEach(option => {
            const foundOption = available_options?.find(t => t.trim().toLowerCase() === option.trim().toLowerCase());
            if (foundOption) {
                // Valid tag styling
                htmlContent += `<span class="tag-pill" style="background-color: grey; color: white; padding: 2px 5px; border-radius: 2px; padding-top: 2px;">${foundOption}</span> `;
            } else {
                // Invalid tag styling
                htmlContent += `<span class="tag-pill invalid-tag" color: #white; padding: 2px 5px; border-radius: 2px; padding-top: 2px;"><img width="18" height="18" style="vertical-align: text-top;" src="https://img.icons8.com/?size=48&id=KarJz0n4bZSj&format=png"></img>${option}</span> `;
                td.style.backgroundColor = '#ff4c44';
            }
        });
        td.innerHTML = htmlContent;
    }
}

function singleRenderer(instance: any, td: HTMLTableCellElement, available_options: string[] | undefined, selected_option: string | undefined, row: number) {
    if (!isDefined(selected_option) || selected_option === '') {
        td.innerHTML = '';
        return;
    }
    const foundOption = available_options?.find(t => t.trim().toLowerCase() === selected_option?.trim().toLowerCase());
    if (foundOption) {
        // Valid tag styling
        td.innerHTML = `<span class="tag-pill" style="background-color: grey; color: white; padding: 2px 5px; border-radius: 2px; padding-top: 2px;">${foundOption}</span> `;
    } else {
        // Invalid tag styling
        td.innerHTML = `<span class="tag-pill invalid-tag" color: #white; padding: 2px 5px; border-radius: 2px; padding-top: 2px;"><img width="18" height="18" style="vertical-align: text-top;" src="https://img.icons8.com/?size=48&id=KarJz0n4bZSj&format=png"></img>${selected_option}</span> `;
        td.style.backgroundColor = '#ff4c44';
    }
}


export const validateTag = (tags: string) => {
    if (!isDefined(tags) || tags === '') return true;
    const tags_array = tags.split(',')
                .map((t) => t.trim())
                .filter((t) => t !== '') ?? [];
    for (const tag of tags_array) {
        if (!isDefined(topicData.allUserTags?.find(t => t.name.trim().toLowerCase() === tag.trim().toLowerCase()))) {
            return false;
        }
    }
    return true;
};

// Checks to see if the place element dropdown is open on screen.
function isAnyPacContainerOpen() {
    for (const container of Array.from(document.querySelectorAll('.pac-container'))) {
        if ((container as HTMLElement).style.display !== 'none') {
            return true;
        }
    }

    return false;
}
